import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { timeClockAPI } from '../utils/api';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import i18n from '../i18n';

const TimeClockPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ isClockedIn: false, current: null });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [time, setTime] = useState(new Date());
  const locale = i18n.language.startsWith('fr') ? fr : enCA;

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, entriesRes] = await Promise.all([
        timeClockAPI.getStatus(),
        timeClockAPI.getAll(),
      ]);
      setStatus(statusRes.data);
      setEntries(entriesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleClockIn = async () => {
    setError(''); setSuccess(''); setActionLoading(true);
    try {
      await timeClockAPI.clockIn({});
      setSuccess(t('timeclock.clockIn') + ' ✓');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally { setActionLoading(false); }
  };

  const handleClockOut = async () => {
    setError(''); setSuccess(''); setActionLoading(true);
    try {
      await timeClockAPI.clockOut();
      setSuccess(t('timeclock.clockOut') + ' ✓');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally { setActionLoading(false); }
  };

  const elapsed = status.current
    ? intervalToDuration({ start: new Date(status.current.clock_in), end: new Date() })
    : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('timeclock.title')}</h2>
          <p className="text-muted">{format(time, 'EEEE d MMMM yyyy', { locale })}</p>
        </div>
      </div>

      {/* Clock Widget */}
      <div className="card timeclock-card" style={{ marginBottom: 32 }}>
        <div className="clock-display">
          {format(time, 'HH:mm:ss')}
        </div>

        <div className={`clock-status ${status.isClockedIn ? 'active' : ''}`}>
          {status.isClockedIn
            ? `${t('timeclock.clockedIn')} — ${status.current?.center_name}`
            : t('timeclock.notClockedIn')}
        </div>

        {elapsed && status.isClockedIn && (
          <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 24 }}>
            {t('timeclock.since')} {format(new Date(status.current.clock_in), 'HH:mm')}
            {' · '}
            {elapsed.hours}h {elapsed.minutes}m {elapsed.seconds}s
          </p>
        )}

        {error && <div className="alert alert-error" style={{ maxWidth: 400, margin: '0 auto 16px' }}>⚠ {error}</div>}
        {success && <div className="alert alert-success" style={{ maxWidth: 400, margin: '0 auto 16px' }}>✓ {success}</div>}

        {status.isClockedIn ? (
          <button className="btn btn-danger btn-lg" onClick={handleClockOut} disabled={actionLoading}
            style={{ minWidth: 200, justifyContent: 'center' }}>
            {actionLoading ? '...' : `⏹ ${t('timeclock.clockOut')}`}
          </button>
        ) : (
          <button className="btn btn-success btn-lg" onClick={handleClockIn} disabled={actionLoading}
            style={{ minWidth: 200, justifyContent: 'center' }}>
            {actionLoading ? '...' : `▶ ${t('timeclock.clockIn')}`}
          </button>
        )}
      </div>

      {/* History */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📋 {t('timeclock.history')}</span>
        </div>
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Employé</th>
                  <th>{t('timeclock.date')}</th>
                  <th>{t('timeclock.entry')}</th>
                  <th>{t('timeclock.exit')}</th>
                  <th>{t('timeclock.duration')}</th>
                  <th>{t('centers.name')}</th>
                  <th>UID du badge</th>
                  <th>{t('timeclock.source')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{t('common.noData')}</td></tr>
                )}
                {entries.map(e => (
                  <tr key={e.id}>
                    {(() => {
                      const isAutoTransferOut = typeof e.notes === 'string'
                        && e.notes.toLowerCase().includes('auto out transfert centre');
                      return (
                        <>
                    <td>
                      <div style={{ fontWeight: 600 }}>{e.first_name} {e.last_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{e.employee_number}</div>
                    </td>
                    <td>{format(new Date(e.clock_in), 'dd/MM/yyyy')}</td>
                    <td style={{ fontWeight: 600 }}>{format(new Date(e.clock_in), 'HH:mm')}</td>
                    <td>{e.clock_out ? format(new Date(e.clock_out), 'HH:mm') : <span className="badge badge-success">En cours</span>}</td>
                    <td>{e.clock_out ? `${parseFloat(e.hours_worked).toFixed(2)}h` : '—'}</td>
                    <td style={{ fontSize: 13 }}>{e.center_name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.badge_uid || '—'}</td>
                    <td>
                      <span className="badge badge-gray">{e.source}</span>
                      {isAutoTransferOut && (
                        <div style={{ marginTop: 6 }}>
                          <span className="badge badge-warning">Sortie auto (transfert centre)</span>
                        </div>
                      )}
                    </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeClockPage;
